import React, { useMemo, useState, useEffect, useRef } from 'react';
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import { shortFunction, file, rate } from "../../translate";
import Api from "../../Api";
import { REACT_APP_API_URL } from "../../config";
const FileUploader = ({
    open,
    onClose,
    loading = false,
    onUpload,        // (file) => void
    onSelect,        // (file) => void
    onSave,          // (file, target) => void
    selectedId,
    target,          // 'before' | 'after' | undefined
    files,
    listLabel,
}) => {
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const [localFile, setLocalFile] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [playingId, setPlayingId] = useState(null);
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null);
    const keySuffix = '&key=akNwNXN1U2k4bzdEQ09QU29KMUpYbXFiQTV5c3FmUm1MOUNheW5qWlYwOD0=';
    const getAudioSrc = (f) => {
        const fid = f?.fname || f?.id || f?.name;
        return fid ? `${REACT_APP_API_URL}/rest/files/audio?file=${fid}${keySuffix}` : null;
    };
    const chooseCompLabel = useMemo(() => rate.choose_comp?.[langIndex] || file.choose?.[langIndex] || 'Choose from computer', [langIndex]);
    const chooseExistLabel = useMemo(() => listLabel || rate.choose_file?.[langIndex] || 'Choose from existing files', [langIndex, listLabel]);
   
    const handleLocalChange = (ev) => {
        const f = ev.target.files?.[0] || null;
        setLocalFile(f);
        if (f) setSelectedFile(null);
        if (f && onUpload) {
            onUpload(f);
        }
    };
    useEffect(() => {
        if (!open) {
            setLocalFile(null);
            setSelectedFile(null);
            return;
        }
        const found = files.find((f) => {
            const fid = f.id || f.fname || f.name;
            return selectedId && fid === selectedId;
        }) || null;
        setSelectedFile(found);
    }, [open, files, selectedId]);
    const cleanupAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        if (audioUrlRef.current) {
            window.URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
        setPlayingId(null);
    };
    useEffect(() => {
        return () => cleanupAudio();
    }, []);
    const handlePlay = async (f, ev) => {
        ev.stopPropagation();
        const src = getAudioSrc(f);
        const fid = f.fname || f.id || f.name;
        if (!src || !fid) return;
        // stop current if same fid
        if (playingId === fid) {
            cleanupAudio();
            return;
        }
        try {
            cleanupAudio();
            const audio = new Audio(src);
            audioRef.current = audio;
            audioUrlRef.current = null;
            setPlayingId(fid);
            audio.onended = cleanupAudio;
            audio.onerror = cleanupAudio;
            audio.play().catch(() => cleanupAudio());
        } catch (e) {
            console.log(e);
            cleanupAudio();
        }
    };
    const handleDownload = async (f, ev) => {
        ev.stopPropagation();
        try {
            const uid = f.fname || f.id || f.name;
            if (!uid) return;
            const audioUrl = `${REACT_APP_API_URL}/rest/files/audio?file=${uid}${keySuffix}`;
            const res = await fetch(audioUrl);
            const blob = await res.blob();
            const objectUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `${f.name || f.fname || uid}.mp3`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(objectUrl);
        } catch (e) {
            console.log(e);
        }
    };
    const handleDelete = async (f, ev) => {
        ev.stopPropagation();
        try {
            const fid = f.id;
            if (!fid) return;
            await Api.fileDelete(fid);
            setFiles(prev => prev.filter(item => (item.id || item.fname || item.name) !== (f.id || f.fname || f.name)));
            if (selectedId && (selectedId === (f.id || f.fname || f.name))) {
                onSelect?.(null);
            }
        } catch (e) {
            console.log(e);
        }
    };

    return (
        <Modal
            aria-labelledby="file-uploader-title"
            aria-describedby="file-uploader-description"
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{ backdrop: { timeout: 300 } }}
        >
            <Fade in={open}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 620,
                    maxHeight: '75vh',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <Typography id="file-uploader-title" variant="h6" sx={{ m: 0 }}>
                            {shortFunction.rate?.[langIndex] || 'Upload file'}
                        </Typography>
                        <Button variant="text" size="small" onClick={onClose}>✕</Button>
                    </div>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            component="label"
                            sx={{ textTransform: 'none', color: '#7ebc0a', borderColor: '#7ebc0a', '&:hover': { borderColor: '#6aa507', color: '#6aa507' } }}
                        >
                            {localFile ? localFile.name : chooseCompLabel}
                            <input
                                type="file"
                                hidden
                                onChange={handleLocalChange}
                                accept=".wav,.mp3,audio/wav,audio/mpeg"
                            />
                        </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.'}
                    </Typography>

                    <Divider />

                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {chooseExistLabel} ({files.length})
                    </Typography>
                    <Box sx={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 2,
                        maxHeight: '40vh',
                        overflowY: 'auto',
                    }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : files.length ? (
                            files.map(f => {
                                const fid = f.id || f.fname || f.name;
                                const rateTarget = target === 'before' ? 1 : target === 'after' ? 2 : null;
                                const isSelected = selectedId
                                    ? selectedId === fid
                                    : (rateTarget !== null && Number(f.rate) === rateTarget);
                                return (
                                    <Box
                                        key={fid}
                                        sx={{
                                            p: '8px 10px',
                                            borderBottom: '1px solid #f4f4f4',
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? 'rgba(126,188,10,0.08)' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8
                                        }}
                                        onClick={() => {
                                            setLocalFile(null);
                                            setSelectedFile(f);
                                            onSelect?.(f);
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            checked={isSelected}
                                            onChange={() => {
                                                setLocalFile(null);
                                                setSelectedFile(f);
                                                onSelect?.(f);
                                            }}
                                            style={{ accentColor: '#7ebc0a' }}
                                        />
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {f.name || f.fname || fid}
                                            </Typography>
                                            {f.notes ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    {f.notes}
                                                </Typography>
                                            ) : null}
                                        </div>
                                        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={playingId === fid ? "Stop" : "Play"}>
                                                <IconButton size="small" onClick={(ev) => handlePlay(f, ev)}>
                                                    {playingId === fid ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Download">
                                                <IconButton size="small" onClick={(ev) => handleDownload(f, ev)}>
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" onClick={(ev) => handleDelete(f, ev)}>
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })
                        ) : (
                            <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: '#888' }}>
                                {rate.file_empty?.[langIndex] || 'No data'}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            sx={{ textTransform: 'none', color: '#7ebc0a', borderColor: '#7ebc0a', '&:hover': { borderColor: '#6aa507', color: '#6aa507' } }}
                            onClick={onClose}
                        >
                            {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ textTransform: 'none', backgroundColor: '#7ebc0a', '&:hover': { backgroundColor: '#6aa507' } }}
                            disabled={!localFile && !selectedFile}
                            onClick={() => onSave?.(localFile || selectedFile, target)}
                        >
                            {shortFunction.operator_save?.[langIndex] || 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default FileUploader;

